/**
 * Éditeur réutilisable de visual_blocks d'un exercice.
 *
 * Extrait de apps/app/src/routes/_app.tutor.exercises.$exerciseId.edit.tsx
 * pour pouvoir être partagé entre :
 *   - éditeur V1 (existant)
 *   - éditeur V2 (BlockShell — un visuel par bloc statement/question/correction)
 *
 * Fournit :
 *   - Extraction depuis une image (edge fn `extract-visual-blocks`)
 *   - Liste des blocs triés par `order` avec mini-toolbar :
 *       éditer JSON · copier id · monter · descendre · supprimer
 *   - Édition inline des champs title et caption
 *   - Bouton « + Ajouter un bloc manuellement » (insère un `unknown_visual`)
 *   - Modal d'édition JSON par bloc
 *
 * État local : flag extraction, message d'erreur, index du bloc en cours
 * d'édition JSON. Toutes les mutations passent par `onChange`.
 */

import { useState } from 'react';
import {
  Braces,
  ChevronDown,
  ChevronUp,
  Code2,
  Copy,
  Image as ImageIcon,
  Pencil,
  Puzzle,
  Ruler,
  Sparkles,
  SquareSplitVertical,
  Trash2,
  Video as VideoIcon,
  Waypoints,
  X,
  Youtube,
} from 'lucide-react';
import type {
  ConstrainedGeometryBlock,
  ExerciseVisualBlock,
  GeometryFigureBlock,
  PythonCodeBlock,
  ScratchCodeBlock,
} from '@jeraisonne/ai';
import { geometryFigureToConstrained } from '@jeraisonne/ai';
import {
  cn,
  VisualBlockRenderer,
  extractYoutubeStart,
  extractYoutubeVideoId,
} from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { PythonCodeForm } from './forms/python-code-form';
import { ScratchCodeForm } from './forms/scratch-code-form';
import { ConstrainedGeometryForm } from './forms/constrained-geometry-form';
import { makeConstrainedGeometryBlock, makePythonBlock, makeScratchBlock } from './forms/defaults';

/** Types disposant d'un formulaire d'édition dédié (sinon édition JSON). */
const FORM_TYPES = new Set(['python_code', 'scratch_code', 'constrained_geometry']);

export interface VisualBlocksEditorProps {
  blocks: ExerciseVisualBlock[];
  onChange: (next: ExerciseVisualBlock[]) => void;
  isDisabled?: boolean;
  /**
   * Si fourni, affiche un bouton « Isoler » par visuel (éditeur V2 uniquement) :
   * détache le visuel de son bloc pour le poser seul dans un nouveau bloc énoncé.
   */
  onIsolate?: (block: ExerciseVisualBlock) => void;
}

/** Bucket Storage utilisé pour les uploads (image / vidéo). Public. */
const UPLOAD_BUCKET = 'illustrations';

/**
 * Upload un fichier dans `illustrations` sous `{userId}/visual-blocks/...`
 * et retourne l'URL publique. Path = userId + visual-blocks + timestamp-name
 * (compatible avec la RLS qui exige `auth.uid()::text = foldername(name)[1]`
 * pour les non-admins — cf. migration 0079).
 */
async function uploadToStorage(file: File): Promise<string> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData?.user) throw new Error("Non authentifié — impossible d'uploader.");
  const userId = userData.user.id;
  const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
  const path = `${userId}/visual-blocks/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage
    .from(UPLOAD_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw new Error(upErr.message);
  const { data } = supabase.storage.from(UPLOAD_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export function VisualBlocksEditor({
  blocks,
  onChange,
  isDisabled = false,
  onIsolate,
}: VisualBlocksEditorProps) {
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState<null | 'image' | 'video'>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingJson, setEditingJson] = useState<string>('');
  /** Index du bloc édité via un formulaire dédié (python/scratch/géométrie). */
  const [formIdx, setFormIdx] = useState<number | null>(null);
  /**
   * Brouillon LOCAL du bloc en cours d'édition dans le modal. Les saisies ne
   * remontent PAS au parent (donc pas d'écriture BDD) tant que l'enseignant n'a
   * pas validé : la persistance se fait en UNE fois à la fermeture (closeForm).
   */
  const [formDraft, setFormDraft] = useState<ExerciseVisualBlock | null>(null);

  const sorted = [...blocks].sort((a, b) => a.order - b.order);

  // ── Helpers de mutation (préservent l'ordre, opèrent sur la liste triée) ──

  const replaceById = (id: string, next: ExerciseVisualBlock) =>
    onChange(blocks.map((b) => (b.id === id ? next : b)));

  const removeById = (id: string) => onChange(blocks.filter((b) => b.id !== id));

  const moveAt = (idx: number, dir: -1 | 1) => {
    const arr = [...sorted];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx]!.order, arr[target]!.order] = [arr[target]!.order, arr[idx]!.order];
    onChange([...arr]);
  };

  const patchField = (id: string, patch: Partial<ExerciseVisualBlock>) =>
    onChange(blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as ExerciseVisualBlock) : b)));

  // ── Extraction LLM depuis une image ───────────────────────────────────────

  async function handleExtract(file: File) {
    setError(null);
    setExtracting(true);
    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const base64 = btoa(binary);
      const mediaType = file.type as 'image/jpeg' | 'image/png' | 'image/webp';

      const { data, error: invokeErr } = await supabase.functions.invoke('extract-visual-blocks', {
        body: { image_base64: base64, media_type: mediaType },
      });
      if (invokeErr || data?.error) {
        throw new Error(data?.error ?? invokeErr?.message ?? "Erreur d'extraction");
      }
      const newBlocks: ExerciseVisualBlock[] = Array.isArray(data?.visual_blocks)
        ? data.visual_blocks
        : [];
      if (newBlocks.length === 0) {
        setError('Aucun élément visuel détecté dans cette image.');
        return;
      }
      const existingIds = new Set(blocks.map((b) => b.id));
      const deduped = newBlocks.map((b) => ({
        ...b,
        id: existingIds.has(b.id) ? `${b.id}-${Date.now()}` : b.id,
        order: blocks.length + b.order,
      }));
      onChange([...blocks, ...deduped]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'extraction visuelle");
    } finally {
      setExtracting(false);
    }
  }

  function handleAddBlank() {
    const newBlock: ExerciseVisualBlock = {
      id: `block-${Date.now()}`,
      type: 'unknown_visual',
      order: blocks.length + 1,
      reason: 'Bloc ajouté manuellement',
    };
    onChange([...blocks, newBlock]);
  }

  /** Ajoute un bloc à formulaire dédié et ouvre directement son éditeur. */
  function handleAddForm(make: (order: number) => ExerciseVisualBlock) {
    const newBlock = make(blocks.length + 1);
    const next = [...blocks, newBlock];
    onChange(next);
    // Le nouveau bloc est en dernière position (tri par order) → ouvre son form
    // sur un brouillon local (édité hors-BDD jusqu'à validation).
    const idx = [...next].sort((a, b) => a.order - b.order).findIndex((b) => b.id === newBlock.id);
    setFormIdx(idx);
    setFormDraft(newBlock);
  }

  async function handleUploadImage(file: File) {
    setError(null);
    setUploading('image');
    try {
      const src = await uploadToStorage(file);
      const newBlock: ExerciseVisualBlock = {
        id: `img-${Date.now()}`,
        type: 'image_visual',
        order: blocks.length + 1,
        src,
        alt: file.name,
      };
      onChange([...blocks, newBlock]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'upload de l'image");
    } finally {
      setUploading(null);
    }
  }

  async function handleUploadVideo(file: File) {
    setError(null);
    setUploading('video');
    try {
      const src = await uploadToStorage(file);
      const newBlock: ExerciseVisualBlock = {
        id: `vid-${Date.now()}`,
        type: 'video_upload',
        order: blocks.length + 1,
        src,
        mime: file.type || undefined,
        title: file.name,
      };
      onChange([...blocks, newBlock]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'upload de la vidéo");
    } finally {
      setUploading(null);
    }
  }

  function handleAddYoutube() {
    const url = prompt("Colle l'URL YouTube (ou l'identifiant 11 caractères) :");
    if (!url) return;
    const videoId = extractYoutubeVideoId(url);
    if (!videoId) {
      setError(
        'URL YouTube non reconnue (formats acceptés : watch?v=, youtu.be/, /embed/, /shorts/, /live/).',
      );
      return;
    }
    setError(null);
    const start = extractYoutubeStart(url);
    const newBlock: ExerciseVisualBlock = {
      id: `yt-${videoId}-${Date.now()}`,
      type: 'youtube_embed',
      order: blocks.length + 1,
      url: url.trim(),
      video_id: videoId,
      ...(start ? { start } : {}),
    };
    onChange([...blocks, newBlock]);
  }

  const editing = editingIdx !== null ? (sorted[editingIdx] ?? null) : null;
  const formEditing = formIdx !== null ? (sorted[formIdx] ?? null) : null;

  /** Ouvre le modal d'édition sur le bloc `idx` (initialise le brouillon local). */
  const openFormAt = (idx: number) => {
    setFormIdx(idx);
    setFormDraft(sorted[idx] ?? null);
  };
  /** Ferme le modal en VALIDANT : un seul commit du brouillon → onChange (→ BDD). */
  const closeForm = () => {
    if (formDraft && formEditing) replaceById(formEditing.id, formDraft);
    setFormIdx(null);
    setFormDraft(null);
  };

  /**
   * Convertit un `geometry_figure` (figure statique) en `constrained_geometry`
   * (figure interactive, mêmes points/tracés, sans contrainte) puis ouvre son
   * formulaire pour que l'auteur ajoute les contraintes. La conversion est
   * persistée immédiatement (replaceById) ; le formulaire re-commit à la
   * validation.
   */
  const convertToConstrained = (idx: number, block: GeometryFigureBlock) => {
    const converted = geometryFigureToConstrained(block);
    replaceById(block.id, converted);
    setFormIdx(idx);
    setFormDraft(converted);
  };

  return (
    <div className="space-y-4">
      {/* Extraction depuis image */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          Extraire depuis une image
        </p>
        <label
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-3 transition-colors hover:border-indigo-300',
            (extracting || isDisabled) && 'pointer-events-none opacity-50',
          )}
        >
          <Sparkles className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="text-sm text-slate-500">
            {extracting ? 'Extraction en cours…' : 'Choisir une image à analyser'}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={extracting || isDisabled}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleExtract(f);
              e.target.value = '';
            }}
          />
        </label>
        {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      </div>

      {/* Liste des blocs */}
      {sorted.length > 0 ? (
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            Blocs ({sorted.length})
          </p>
          {sorted.map((block, i) => (
            <div
              key={block.id}
              className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <span className="select-all rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                  #{block.id}
                </span>
                <span className="text-xs text-slate-400">{block.type}</span>
                <div className="ml-auto flex items-center gap-1">
                  {FORM_TYPES.has(block.type) ? (
                    <button
                      type="button"
                      title="Éditer (formulaire)"
                      disabled={isDisabled}
                      onClick={() => openFormAt(i)}
                      className="min-h-9 min-w-9 rounded p-1 text-indigo-500 hover:text-indigo-700 disabled:opacity-30"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    title="Éditer JSON"
                    disabled={isDisabled}
                    onClick={() => {
                      setEditingIdx(i);
                      setEditingJson(JSON.stringify(block, null, 2));
                    }}
                    className="min-h-9 min-w-9 rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    {FORM_TYPES.has(block.type) ? (
                      <Braces className="h-3.5 w-3.5" />
                    ) : (
                      <Pencil className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    title="Copier l'ID"
                    onClick={() => navigator.clipboard.writeText(block.id)}
                    className="min-h-9 min-w-9 rounded p-1 text-slate-400 hover:text-slate-700"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Monter"
                    disabled={i === 0 || isDisabled}
                    onClick={() => moveAt(i, -1)}
                    className="min-h-9 min-w-9 rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Descendre"
                    disabled={i === sorted.length - 1 || isDisabled}
                    onClick={() => moveAt(i, 1)}
                    className="min-h-9 min-w-9 rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {block.type === 'geometry_figure' ? (
                    <button
                      type="button"
                      title="Convertir en géométrie de contrainte (figure interactive)"
                      disabled={isDisabled}
                      onClick={() => convertToConstrained(i, block as GeometryFigureBlock)}
                      className="inline-flex min-h-9 items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800 disabled:opacity-30"
                    >
                      <Waypoints className="h-3.5 w-3.5" />
                      Contraindre
                    </button>
                  ) : null}
                  {onIsolate ? (
                    <button
                      type="button"
                      title="Isoler — déplacer ce visuel seul dans un nouveau bloc énoncé"
                      disabled={isDisabled}
                      onClick={() => onIsolate(block)}
                      className="inline-flex min-h-9 items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 hover:text-indigo-800 disabled:opacity-30"
                    >
                      <SquareSplitVertical className="h-3.5 w-3.5" />
                      Isoler
                    </button>
                  ) : null}
                  <button
                    type="button"
                    title="Supprimer"
                    disabled={isDisabled}
                    onClick={() => removeById(block.id)}
                    className="min-h-9 min-w-9 rounded p-1 text-red-500 hover:text-red-700 disabled:opacity-30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Titre (optionnel)"
                  value={block.title ?? ''}
                  disabled={isDisabled}
                  onChange={(e) => patchField(block.id, { title: e.target.value || undefined })}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <input
                  type="text"
                  placeholder="Légende (optionnel)"
                  value={block.caption ?? ''}
                  disabled={isDisabled}
                  onChange={(e) => patchField(block.id, { caption: e.target.value || undefined })}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <div className="rounded-lg border border-slate-100 p-3">
                <VisualBlockRenderer block={block} />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          Ajouter
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label
            className={cn(
              'flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50',
              (uploading === 'image' || isDisabled) && 'pointer-events-none opacity-50',
            )}
          >
            <ImageIcon className="h-4 w-4" />
            {uploading === 'image' ? 'Upload…' : 'Image'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
              className="hidden"
              disabled={uploading !== null || isDisabled}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUploadImage(f);
                e.target.value = '';
              }}
            />
          </label>
          <label
            className={cn(
              'flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50',
              (uploading === 'video' || isDisabled) && 'pointer-events-none opacity-50',
            )}
          >
            <VideoIcon className="h-4 w-4" />
            {uploading === 'video' ? 'Upload…' : 'Vidéo'}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/ogg"
              className="hidden"
              disabled={uploading !== null || isDisabled}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUploadVideo(f);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            onClick={handleAddYoutube}
            disabled={isDisabled}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <Youtube className="h-4 w-4 text-red-600" />
            YouTube
          </button>
          <button
            type="button"
            onClick={() => handleAddForm(makePythonBlock)}
            disabled={isDisabled}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <Code2 className="h-4 w-4 text-sky-600" />
            Python
          </button>
          <button
            type="button"
            onClick={() => handleAddForm(makeScratchBlock)}
            disabled={isDisabled}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <Puzzle className="h-4 w-4 text-orange-500" />
            Scratch
          </button>
          <button
            type="button"
            onClick={() => handleAddForm(makeConstrainedGeometryBlock)}
            disabled={isDisabled}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <Ruler className="h-4 w-4 text-indigo-600" />
            Géométrie
          </button>
          <button
            type="button"
            onClick={handleAddBlank}
            disabled={isDisabled}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            + Vide
          </button>
        </div>
      </div>

      {/* Modal d'édition JSON */}
      {editing ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
          onClick={() => setEditingIdx(null)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-[15px] font-bold text-slate-900">Éditer JSON</p>
                <p className="mt-0.5 font-mono text-xs text-slate-400">#{editing.id}</p>
              </div>
              <button
                type="button"
                onClick={() => setEditingIdx(null)}
                className="min-h-9 min-w-9 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="mb-4 rounded-lg border border-slate-100 p-3">
                <VisualBlockRenderer block={editing} />
              </div>
              <textarea
                rows={12}
                value={editingJson}
                onChange={(e) => setEditingJson(e.target.value)}
                className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="flex gap-3 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setEditingIdx(null)}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 sm:flex-none"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(editingJson) as ExerciseVisualBlock;
                    replaceById(editing.id, parsed);
                    setEditingIdx(null);
                  } catch {
                    alert('JSON invalide');
                  }
                }}
                className="flex-1 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-700 active:scale-[.98] sm:flex-none"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal d'édition par formulaire dédié */}
      {formDraft ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
          onClick={closeForm}
        >
          <div
            className={`flex max-h-[88vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl ${
              formDraft.type === 'constrained_geometry' ? 'max-w-4xl' : 'max-w-xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-[15px] font-bold text-slate-900">
                  {formDraft.type === 'python_code'
                    ? 'Code Python'
                    : formDraft.type === 'scratch_code'
                      ? 'Code Scratch'
                      : 'Figure géométrique sous contraintes'}
                </p>
                <p className="mt-0.5 font-mono text-xs text-slate-400">#{formDraft.id}</p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="min-h-9 min-w-9 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {formDraft.type === 'python_code' ? (
                <>
                  <PythonCodeForm block={formDraft as PythonCodeBlock} onChange={setFormDraft} />
                  <div className="mt-4 rounded-lg border border-slate-100 p-3">
                    <VisualBlockRenderer block={formDraft} />
                  </div>
                </>
              ) : formDraft.type === 'scratch_code' ? (
                <>
                  <ScratchCodeForm block={formDraft as ScratchCodeBlock} onChange={setFormDraft} />
                  <div className="mt-4 rounded-lg border border-slate-100 p-3">
                    <VisualBlockRenderer block={formDraft} />
                  </div>
                </>
              ) : formDraft.type === 'constrained_geometry' ? (
                <ConstrainedGeometryForm
                  block={formDraft as ConstrainedGeometryBlock}
                  onChange={setFormDraft}
                />
              ) : null}
            </div>

            <div className="flex items-center gap-3 border-t border-slate-100 px-5 py-4">
              <p className="flex-1 text-[11px] text-slate-400">
                Les modifications sont enregistrées à la validation.
              </p>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-700 active:scale-[.98]"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
